import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { Household, Transaction, Category } from '../types';
import { 
  Users, 
  Plus, 
  Copy, 
  Share2,
  ArrowRight, 
  Trash2, 
  ShieldX, 
  ChevronRight,
  CheckCircle2
} from 'lucide-react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const Groups: React.FC = () => {
  const [households, setHouseholds] = useState<Household[]>([]);
  const [loading, setLoading] = useState(true);
  const [isJoining, setIsJoining] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [activeTab, setActiveTab] = useState<'my' | 'create' | 'join'>('my');
  const [groupStats, setGroupStats] = useState<Record<string, { total: number, catBreakdown: any[] }>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchHouseholds();
  }, []);

  const fetchHouseholds = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    setCurrentUserId(user.id);

    const { data: memberRecords } = await supabase.from('household_members').select('household_id').eq('user_id', user.id);
    
    if (memberRecords && memberRecords.length > 0) {
      const ids = memberRecords.map(r => r.household_id);
      const { data: hhs } = await supabase.from('households').select('*').in('id', ids);
      
      if (hhs) {
        setHouseholds(hhs);
        
        const stats: any = {};
        const start = format(startOfMonth(new Date()), 'yyyy-MM-dd');
        const end = format(endOfMonth(new Date()), 'yyyy-MM-dd');

        for (const hh of hhs) {
          const { data: txs } = await supabase.from('transactions').select('*').eq('household_id', hh.id).gte('date', start).lte('date', end);
          if (txs) {
            const total = txs.reduce((acc, t) => (t.type === 'expense' || t.type === 'debt') ? acc + Number(t.amount) : acc, 0);
            const cats: any = {};
            txs.forEach(t => {
               if (t.type === 'expense' || t.type === 'debt') cats[t.category] = (cats[t.category] || 0) + Number(t.amount);
            });
            stats[hh.id] = { 
              total, 
              catBreakdown: Object.entries(cats).sort((a:any, b:any) => b[1] - a[1]).slice(0, 3) 
            };
          }
        }
        setGroupStats(stats);
      }
    } else {
      setHouseholds([]);
    }
    setLoading(false);
  };

  const copyToClipboard = async (e: React.MouseEvent, code: string) => {
    e.stopPropagation();
    await navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const shareInvite = async (e: React.MouseEvent, hh: Household) => {
    e.stopPropagation();
    // Updated to use the hardcoded production URL
    const shareUrl = `https://www.spendingwise.in/#/join/${hh.invite_code}`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Join ${hh.name} on SecureTrack`,
          text: `Join our shared expense group using this link:`,
          url: shareUrl
        });
      } catch (err) {
        console.log("Sharing cancelled");
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert("Group Link copied to clipboard!");
    }
  };

  const leaveGroup = async (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    if (!window.confirm(`Leave group "${name}"? You will be removed from this list, but the group will remain active for other users.`)) return;
    
    try {
      const { error } = await supabase
        .from('household_members')
        .delete()
        .eq('household_id', id)
        .eq('user_id', currentUserId);

      if (error) throw error;
      fetchHouseholds();
    } catch (err: any) {
      alert("Action failed: " + err.message);
    }
  };

  const createHousehold = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user || !newName) return;
    setIsCreating(true);
    const code = Math.random().toString(36).substring(2, 8).toUpperCase();
    
    try {
      const { data, error } = await supabase.from('households').insert([{ name: newName, invite_code: code, created_by: user.id }]).select().single();
      if (error) throw error;
      if (data) {
        await supabase.from('household_members').insert([{ household_id: data.id, user_id: user.id }]);
        setNewName('');
        setActiveTab('my');
        fetchHouseholds();
      }
    } catch (err: any) {
      alert("Error creating group: " + err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const joinHousehold = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const cleanCode = inviteCode.trim().toUpperCase();
    if (!user || !cleanCode) return;
    
    setIsJoining(true);
    try {
      const { data: hh, error: fetchError } = await supabase
        .from('households')
        .select('id, name')
        .eq('invite_code', cleanCode)
        .single();
      
      if (fetchError || !hh) {
        alert("Invalid code. Please check and try again.");
        setIsJoining(false);
        return;
      }

      const { error: joinError } = await supabase.from('household_members').upsert({ household_id: hh.id, user_id: user.id });
      if (joinError) throw joinError;
      
      setInviteCode('');
      setActiveTab('my');
      fetchHouseholds();
      alert(`Successfully joined ${hh.name}`);
    } catch (err: any) {
      alert("Connection failed: " + err.message);
    } finally {
      setIsJoining(false);
    }
  };

  if (loading) return <div className="pt-20 text-center"><div className="animate-spin h-10 w-10 border-b-2 border-slate-900 mx-auto rounded-full"></div></div>;

  return (
    <div className="space-y-6 pb-20">
      <header className="px-1">
        <h1 className="text-2xl font-black text-slate-900">Groups</h1>
        <p className="text-slate-400 text-sm font-medium">Track shared expenses with others</p>
      </header>

      <div className="flex bg-slate-200/50 p-1.5 rounded-3xl border border-slate-200 shadow-inner">
        <button onClick={() => setActiveTab('my')} className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'my' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500'}`}>My Groups</button>
        <button onClick={() => setActiveTab('join')} className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'join' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500'}`}>Join Group</button>
        <button onClick={() => setActiveTab('create')} className={`flex-1 py-3.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'create' ? 'bg-[#0f172a] text-white shadow-xl' : 'text-slate-500'}`}>New Group</button>
      </div>

      {activeTab === 'my' && (
        <div className="space-y-6">
          {households.length === 0 ? (
            <div className="bg-white p-16 rounded-[3rem] text-center border border-dashed border-slate-200">
              <ShieldX className="w-16 h-16 text-slate-100 mx-auto mb-4" />
              <p className="text-slate-400 font-bold uppercase text-[10px] tracking-widest">No active groups</p>
            </div>
          ) : (
            households.map(hh => (
              <div 
                key={hh.id} 
                onClick={() => navigate(`/groups/${hh.id}`)}
                className="bg-white p-8 rounded-[2.5rem] border border-slate-100 shadow-xl shadow-slate-200/50 space-y-6 relative overflow-hidden group cursor-pointer active:scale-[0.99] transition-all hover:border-slate-300"
              >
                <div className="flex justify-between items-start relative z-10">
                  <div className="flex-1">
                    <h3 className="text-xl font-black text-slate-900 leading-none">{hh.name}</h3>
                    <div className="flex flex-wrap items-center mt-4 gap-2">
                       <button 
                        onClick={(e) => shareInvite(e, hh)} 
                        className="flex items-center text-[9px] font-black uppercase tracking-widest bg-amber-50 text-amber-600 px-4 py-2 rounded-xl border border-amber-100 hover:bg-amber-100 transition-all active:scale-95"
                      >
                        <Share2 size={12} className="mr-2" /> Share Group Link
                      </button>
                      <button 
                         onClick={(e) => copyToClipboard(e, hh.invite_code)}
                         className="flex items-center text-[9px] font-black uppercase tracking-widest bg-slate-50 text-slate-600 px-4 py-2 rounded-xl border border-slate-100 hover:bg-slate-100 transition-all active:scale-95"
                       >
                         {copiedCode === hh.invite_code ? <CheckCircle2 size={12} className="mr-2 text-emerald-500" /> : <Copy size={12} className="mr-2" />}
                         Code: {hh.invite_code}
                       </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={(e) => leaveGroup(e, hh.id, hh.name)} 
                      className="bg-red-50 text-red-500 p-3.5 rounded-2xl border border-red-100 hover:bg-red-500 hover:text-white transition-all shadow-sm active:scale-90"
                    >
                      <Trash2 size={20} />
                    </button>
                    <div className="bg-[#0f172a] text-amber-400 p-3.5 rounded-2xl border border-white/10 shadow-lg group-hover:translate-x-1 transition-transform">
                      <ChevronRight size={24} />
                    </div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 gap-4 pt-2">
                  <div className="bg-[#0f172a] p-6 rounded-[2rem] text-white shadow-2xl relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-32 h-32 bg-amber-400/5 blur-[50px] -translate-y-1/2 translate-x-1/2"></div>
                    <div className="flex justify-between items-center mb-4">
                       <p className="text-[10px] font-black uppercase opacity-60 tracking-widest">Total Spent</p>
                       <span className="text-sm font-black text-amber-400">₹{groupStats[hh.id]?.total.toLocaleString()}</span>
                    </div>
                    <div className="space-y-3">
                      {(groupStats[hh.id]?.catBreakdown || []).length > 0 ? (groupStats[hh.id]?.catBreakdown || []).map(([cat, val]: any) => (
                        <div key={cat} className="flex items-center justify-between">
                          <span className="text-[10px] font-black uppercase text-slate-400 tracking-wider w-16">{cat}</span>
                          <div className="flex-1 mx-4 h-1.5 bg-white/10 rounded-full overflow-hidden">
                             <div className="h-full bg-amber-400" style={{ width: `${(val / (groupStats[hh.id]?.total || 1)) * 100}%` }}></div>
                          </div>
                          <span className="text-[10px] font-black text-white w-12 text-right">₹{val.toLocaleString()}</span>
                        </div>
                      )) : (
                        <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest text-center py-2 italic">No activity yet</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {activeTab === 'create' && (
        <div className="bg-white p-10 rounded-[3rem] shadow-xl border border-slate-100 space-y-8 max-w-md mx-auto">
          <div className="text-center">
             <div className="w-20 h-20 bg-slate-900 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-xl shadow-slate-200">
                <Plus className="text-amber-400 w-10 h-10" />
             </div>
             <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">New Group</h2>
             <p className="text-slate-400 text-sm mt-2">Create a group for shared expenses</p>
          </div>
          <input type="text" placeholder="e.g. Room Expenses" className="w-full p-6 bg-slate-50 border border-slate-100 rounded-[2rem] outline-none text-center font-bold text-slate-800 text-lg" value={newName} onChange={e => setNewName(e.target.value)} />
          <button onClick={createHousehold} disabled={isCreating} className="w-full bg-[#0f172a] text-amber-400 py-5 rounded-[2.2rem] font-black shadow-2xl flex items-center justify-center disabled:opacity-50">
            {isCreating ? <div className="w-6 h-6 border-2 border-amber-400/30 border-t-amber-400 rounded-full animate-spin"></div> : 'Create Group'}
          </button>
        </div>
      )}

      {activeTab === 'join' && (
        <div className="bg-white p-10 rounded-[3rem] shadow-xl border border-slate-100 space-y-8 max-w-md mx-auto">
          <div className="text-center">
             <div className="w-20 h-20 bg-amber-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 shadow-xl border border-amber-100">
                <Users className="text-amber-600 w-10 h-10" />
             </div>
             <h2 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Join Group</h2>
             <p className="text-slate-400 text-sm mt-2">Enter the invite code</p>
          </div>
          <div className="space-y-4">
             <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest text-center">Invite Code</label>
            <input 
              type="text" 
              placeholder="CODE" 
              maxLength={6}
              className="w-full p-6 bg-slate-50 border border-slate-100 rounded-3xl outline-none focus:ring-4 focus:ring-amber-50 text-center font-black text-4xl uppercase tracking-[0.4em] text-amber-600 shadow-inner" 
              value={inviteCode} 
              onChange={e => setInviteCode(e.target.value.toUpperCase())} 
            />
          </div>
          <button onClick={joinHousehold} disabled={isJoining} className="w-full bg-[#0f172a] text-[#fbbf24] py-6 rounded-[2.2rem] font-black shadow-2xl flex items-center justify-center disabled:opacity-50">
            {isJoining ? <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div> : 'Join Group'}
          </button>
        </div>
      )}
    </div>
  );
};

export default Groups;
